<template>
  <base-sheet :visible="visible" :dismissible="dismissible" @close="onCancel">
    <h3 class="vxl-sheet__title">{{ title }}</h3>
    <p v-if="subtitle" class="vxl-sheet__subtitle">{{ subtitle }}</p>

    <div class="options" role="radiogroup">
      <button
        v-for="opt in options"
        :key="opt.value"
        type="button"
        class="option-btn"
        :class="{ selected: selected === opt.value }"
        role="radio"
        :aria-checked="selected === opt.value"
        @click="selected = opt.value"
      >
        <span class="radio-dot" :style="selected === opt.value ? { borderColor: $theme.primaryColor } : null">
          <span
            v-if="selected === opt.value"
            class="radio-dot-inner"
            :style="{ background: $theme.primaryColor }"
          ></span>
        </span>
        <span class="opt-label">{{ opt.label }}</span>
        <span class="opt-hint">{{ opt.hint }}</span>
      </button>
    </div>

    <div v-if="footnote" class="vxl-sheet__note">{{ footnote }}</div>

    <button
      type="button"
      class="vxl-sheet__action vxl-sheet__action--primary"
      :disabled="!selected"
      :style="{ background: $theme.primaryColor }"
      @click="onConfirm"
    >
      {{ confirmLabel }}
    </button>
    <button
      v-if="dismissible"
      type="button"
      class="vxl-sheet__action vxl-sheet__action--ghost"
      @click="onCancel"
    >
      Cancelar
    </button>
  </base-sheet>
</template>

<script>
// Bottom sheet de escolha de qualidade do download. Padrão consistente com
// LearningObjectActionsSheet/GroupActionsSheet/CourseActionsSheet em
// components/myDownloads/. Renderizado pelo PAI (não invocado por
// $modal.show) — pai controla v-model via `visible` + eventos.
//
// Três modos (prop `mode`):
//
//   - 'firstTime':   onboarding obrigatório (NÃO dismissível). Aparece no 1º
//                    download de vídeo. A escolha vira o padrão Wi-Fi do app.
//   - 'cellular':    abre a cada fila iniciada em rede móvel. Dismissível
//                    (Cancelar volta sem baixar). Pré-selecionado com a última
//                    escolha cellular.
//   - 'editSetting': para uso na página "Meus Downloads" pra alterar o padrão
//                    Wi-Fi fora do fluxo de download. Dismissível.
import BaseSheet from '@/components/base/BaseSheet';

export default {
  name: 'DownloadQualitySheet',
  components: { BaseSheet },
  props: {
    visible: { type: Boolean, default: false },
    mode: {
      type: String,
      default: 'firstTime',
      validator: v => ['firstTime', 'cellular', 'editSetting'].includes(v)
    },
    initialSelected: { type: String, default: null },
    // Pra mode='cellular': qualidade padrão Wi-Fi exibida como nota contextual.
    wifiDefaultQuality: { type: String, default: null }
  },
  data() {
    return {
      selected: this.initialSelected
    };
  },
  computed: {
    dismissible() {
      // firstTime é onboarding — user precisa decidir. Outros modos permitem
      // sair sem escolher.
      return this.mode !== 'firstTime';
    },
    options() {
      return [
        { value: 'low', label: 'Baixa', hint: 'Menos espaço, menos dados' },
        { value: 'medium', label: 'Média', hint: 'Qualidade equilibrada' },
        { value: 'high', label: 'Alta', hint: 'Melhor qualidade visual' }
      ];
    },
    title() {
      if (this.mode === 'cellular') return 'Baixar em rede móvel';
      if (this.mode === 'editSetting') return 'Qualidade padrão dos downloads';
      return 'Escolha a qualidade dos downloads';
    },
    subtitle() {
      if (this.mode === 'cellular') {
        return 'Escolha a qualidade desta fila. A escolha vale só para o download atual.';
      }
      if (this.mode === 'editSetting') {
        return 'Esta qualidade será usada quando você baixar aulas em Wi-Fi.';
      }
      return 'Esta será sua qualidade padrão em Wi-Fi. Você pode alterar a qualquer momento em "Meus Downloads".';
    },
    footnote() {
      if (this.mode !== 'cellular' || !this.wifiDefaultQuality) return null;
      const labels = { low: 'Baixa', medium: 'Média', high: 'Alta' };
      return `Padrão em Wi-Fi: ${labels[this.wifiDefaultQuality] || '—'}`;
    },
    confirmLabel() {
      if (this.mode === 'firstTime') return 'Continuar';
      if (this.mode === 'cellular') return 'Baixar';
      return 'Salvar';
    }
  },
  watch: {
    // Re-sincroniza `selected` quando a sheet (re)abre — o pai pode ter mudado
    // initialSelected entre aberturas consecutivas. (A trava de scroll do body
    // é do BaseSheet.)
    visible(now) {
      if (now) this.selected = this.initialSelected;
    }
  },
  methods: {
    onConfirm() {
      if (!this.selected) return;
      this.$emit('select', this.selected);
    },
    onCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped lang="scss">
.options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.option-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px;
  background: var(--md-surface-elev, rgba(255, 255, 255, 0.04));
  border: 1px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  color: var(--md-text, #fff);
  transition: background 0.15s, border-color 0.15s;

  &:active {
    background: var(--md-row-active, rgba(255, 255, 255, 0.07));
  }

  &.selected {
    background: var(--md-row-active, rgba(255, 255, 255, 0.06));
    // border-color setado inline via $theme.primaryColor pra herdar whitelabel
  }
}

.radio-dot {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--md-border-strong, rgba(255, 255, 255, 0.35));
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.radio-dot-inner {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.opt-label {
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.opt-hint {
  font-size: 12px;
  color: var(--md-text-dim, rgba(255, 255, 255, 0.55));
  margin-left: auto;
  text-align: right;
}
</style>
